import {isEqual} from 'lodash';
import {createSelector} from 'reselect';

import {type RootState} from '../../../types/redux';
import {useSelector} from '../../hooks';
import {type Ref as SectionRef} from '../panelBankSectionConfig/types';
import {makeOptionalPartSelector} from '../selectors';

export const useSectionSettings = (sectionRef: SectionRef) => {
  const sectionSettings = useSelector(
    // oxlint-disable-next-line wandb/prefer-memoized-selector -- uses custom isEqual comparator
    makeSectionSettingsSelector(sectionRef),
    isEqual
    // 'makeWorkspaceSettingsSelector' // comment back in for debugging
  );
  return sectionSettings;
};

export const makeSectionSettingsSelector = (sectionRef: SectionRef) => {
  return (state: RootState) => {
    return state.views.parts['panel-bank-section-config'][sectionRef.id]
      .sectionSettings;
  };
};

export const makeMaybeSectionSettingsSelector = (sectionRef?: SectionRef) => {
  if (sectionRef == null) {
    return () => undefined;
  }

  return makeSectionSettingsSelector(sectionRef);
};

export const makeSectionMediaSettingsSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeMaybeSectionSettingsSelector(sectionRef),
    sectionSettings => sectionSettings?.media
  );

export const makeSectionMediaSliderSyncSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.sliderSync
  );

/** Read sliderSync from a section part (for non-React selectors). */
export const selectSectionMediaSliderSyncFromSection = (
  section: {sectionSettings?: {media?: {sliderSync?: boolean}}} | undefined
) => section?.sectionSettings?.media?.sliderSync;

export const makeSectionMediaSliderKeySelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.mediaSliderKey
  );

export const makeSectionMediaModeSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.mode
  );

export const makeSectionMediaSnapToExistingStepSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.snapToExistingStep
  );

export const makeSectionMediaMaxRunsSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.maxRuns
  );

export const makeSectionMediaMaxGalleryItemsSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.maxGalleryItems
  );

export const makeSectionMediaActualSizeSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.actualSize
  );

export const makeSectionMediaFitToDimensionSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.fitToDimension
  );

export const makeSectionMediaGallerySettingsSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.gallerySettings
  );

export const makeSectionMediaGridSettingsSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.gridSettings
  );

export const makeSectionMediaVideoPlaybackSyncSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.videoPlaybackSync
  );

export const makeSectionMediaVideoPlaybackSyncAutoplaySelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.videoPlaybackSyncAutoplay
  );

export const makeSectionMediaVideoPlaybackSyncLoopSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.videoPlaybackSyncLoop
  );

export const makeSectionMediaCompareSettingsSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.compareSettings
  );

export const makeSectionMediaBoundingBoxConfigSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.boundingBoxConfig
  );

export const makeSectionMediaSegmentationMaskConfigSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.segmentationMaskConfig
  );

export const makeSectionMediaUseRightHandedSystemSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.useRightHandedSystem
  );

export const makeSectionMediaPointColorSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.pointColor
  );

export const makeSectionMediaObject3DBackgroundColorSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.object3DBackgroundColor
  );

export const makeSectionMediaDisableVideoStreamSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.disableVideoStream
  );

export const makeSectionMediaVideoPlayerTimeStepSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.videoPlayerTimeStep
  );

export const makeSectionMediaAlwaysOriginalSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.alwaysOriginal
  );

export const makeSectionMediaMaxYAxisCountSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.maxYAxisCount
  );

export const makeSectionMediaColumnCountSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.columnCount
  );

export const makeSectionMediaPixelatedSelector = (sectionRef?: SectionRef) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.pixelated
  );

export const makeSectionMediaStepStrideLengthSelector = (
  sectionRef?: SectionRef
) =>
  createSelector(
    makeSectionMediaSettingsSelector(sectionRef),
    media => media?.stepStrideLength
  );

/** Read mediaSliderKey from a section part (for non-React selectors). */
export const selectSectionMediaSliderKeyFromSection = (
  section: {sectionSettings?: {media?: {mediaSliderKey?: string}}} | undefined
) => section?.sectionSettings?.media?.mediaSliderKey;

export const makeSectionLinePlotPanelsSelector = (sectionRef?: SectionRef) => {
  return createSelector(
    makeOptionalPartSelector(sectionRef),
    (state: RootState) => state.views.parts.panel,
    (section, panelParts) => {
      const panels = [];
      for (const panelRef of section?.panelRefs ?? []) {
        const panel = panelParts[panelRef.id];

        if (panel.viewType === 'Run History Line Plot') {
          panels.push({
            config: panel.config,
            viewType: panel.viewType,
            __id__: panel.__id__,
            ref: panelRef,
          });
        }
      }
      return panels;
    }
  );
};

export const makeSectionMediaKeysSelector = (sectionRef?: SectionRef) => {
  return createSelector(
    makeOptionalPartSelector(sectionRef),
    (state: RootState) => state.views.parts.panel,
    (section, panelParts) => {
      const mediaKeys = new Set<string>();
      for (const panelRef of section?.panelRefs ?? []) {
        const panel = panelParts[panelRef.id];
        if (panel?.viewType === 'Media Browser' && panel.config.mediaKeys) {
          panel.config.mediaKeys.forEach(key => {
            mediaKeys.add(key);
          });
        }
      }
      return Array.from(mediaKeys);
    }
  );
};
